// Навигация с учётом доступности модулей (колонка «Навигация/панель» §6.1).
//
// Правило показа НЕ переоткрывается: его считает `navVisibilityFor` в
// `@studyhub/shared/module`. Здесь только применение к готовым группам
// навигации — и ровно один раз, чтобы сайдбар, шторка и нижняя навигация не
// разошлись в том, какие разделы человек видит.
//
// Три неочевидности матрицы, которые тут НЕЛЬЗЯ «упростить»:
//   • авария (`killed`) пункт НЕ прячет — исчезнувший раздел читается как
//     потеря данных, а не как «чиним»;
//   • авария внешнего сервиса — то же самое, пункт виден неактивным;
//   • `platform_unsupported` даёт `read_only` (данные видны на своей странице),
//     но пункт скрыт — вести некуда.

import type { ModuleNavPresentation } from "@studyhub/shared/module";
import { useMemo } from "react";
import { useModuleAvailability } from "@/hooks/use-module-availability";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { navPresentationOf } from "@/lib/module-availability/resolve";
import type { NavGroup, NavItem } from "./nav-config";

/** Пункт навигации вместе с тем, как его показывать. */
export interface PresentedNavItem extends Omit<NavItem, "children"> {
	readonly presentation: ModuleNavPresentation;
	/**
	 * Подпункты проходят тот же гейт, что и родитель (см. `presentItems`).
	 * `Omit` у базового типа обязателен: без него `children` остался бы
	 * `NavItem[]`, и в дерево просочились бы пункты без вычисленной
	 * доступности.
	 */
	readonly children?: PresentedNavItem[];
}

export interface PresentedNavGroup extends Omit<NavGroup, "items"> {
	readonly items: PresentedNavItem[];
}

function presentItems(
	items: readonly NavItem[],
	presentationOf: (item: NavItem) => ModuleNavPresentation,
): PresentedNavItem[] {
	const out: PresentedNavItem[] = [];
	for (const item of items) {
		const presentation = presentationOf(item);
		if (presentation === "hidden") continue;
		// Дети гоняются через тот же гейт рекурсивно: подпункт выключенного или
		// недоступного по тарифу модуля обязан исчезнуть вместе с ним, иначе
		// пользователь видит вход в раздел, которого у него нет.
		const children = item.children ? presentItems(item.children, presentationOf) : undefined;
		const { children: _raw, ...rest } = item;
		out.push({
			...rest,
			presentation,
			...(children && children.length > 0 ? { children } : {}),
		});
	}
	return out;
}

/**
 * Группы навигации, отфильтрованные и размеченные по карте доступности.
 *
 * Пустые группы выбрасываются целиком: заголовок «Инструменты» без единого
 * пункта — это шум, который выглядит как сломанная вёрстка.
 */
export function usePresentedNavGroups(groups: readonly NavGroup[]): PresentedNavGroup[] {
	const view = useModuleAvailability();
	const commerceVisible = useCommerceVisible();
	return useMemo(() => {
		const presentationOf = (item: NavItem): ModuleNavPresentation =>
			hiddenAsCommerce(item, commerceVisible)
				? "hidden"
				: item.moduleId
					? navPresentationOf(view, item.moduleId)
					: "visible";
		return groups
			.map((group) => ({ ...group, items: presentItems(group.items, presentationOf) }))
			.filter((group) => group.items.length > 0);
	}, [groups, view, commerceVisible]);
}

/** Плоский список пунктов (нижняя навигация, Cmd-K, витрина возможностей). */
export function usePresentedNavItems(items: readonly NavItem[]): PresentedNavItem[] {
	const view = useModuleAvailability();
	const commerceVisible = useCommerceVisible();
	return useMemo(
		() =>
			presentItems(items, (item) =>
				hiddenAsCommerce(item, commerceVisible)
					? "hidden"
					: item.moduleId
						? navPresentationOf(view, item.moduleId)
						: "visible",
			),
		[items, view, commerceVisible],
	);
}

/**
 * Пункт продажи там, где коммерция не видна (приложение из App Store,
 * guideline 3.1.1), не показывается вовсе — ни в каком меню.
 */
function hiddenAsCommerce(item: NavItem, commerceVisible: boolean): boolean {
	return item.commerce === true && !commerceVisible;
}
